import { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Plus } from "lucide-react";
import { useCatalogsStore } from "../../../store/Catalogs";

interface TariffDialogProps {
	catalogId: string;
	catalogName: string;
	categoryId: string | null;
	categoryName?: string;
	isCustomCatalog: boolean;
}

export const TariffDialog: React.FC<TariffDialogProps> = ({
	catalogId,
	catalogName,
	categoryId,
	categoryName,
	isCustomCatalog,
}) => {
	const [open, setOpen] = useState(false);
	const [code, setCode] = useState("");
	const [codeE, setCodeE] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const addCustomTariff = useCatalogsStore((state) => state.addCustomTariff);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!code.trim() || !codeE.trim()) {
			return;
		}

		setIsSubmitting(true);
		try {
			await addCustomTariff(
				catalogId,
				categoryId,
				code.trim(),
				codeE.trim(),
				isCustomCatalog
			);
			setOpen(false);
			setCode("");
			setCodeE("");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleOpenChange = (newOpen: boolean) => {
		setOpen(newOpen);
		if (!newOpen) {
			setCode("");
			setCodeE("");
		}
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button size="sm" variant="outline" className="h-7 px-2 flex items-center gap-1">
					<Plus className="h-3 w-3" />
					<span className="text-xs">Neue Leistung</span>
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[425px]">
				<form onSubmit={handleSubmit}>
					<DialogHeader>
						<DialogTitle>Neue Leistung erstellen</DialogTitle>
						<DialogDescription>
							Erstellen Sie eine neue Leistung für den Katalog "{catalogName}"
							{categoryName && ` in der Kategorie "${categoryName}"`}.
						</DialogDescription>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid grid-cols-4 items-center gap-4">
							<Label htmlFor="code" className="text-right">
								Code
							</Label>
							<Input
								id="code"
								value={code}
								onChange={(e) => setCode(e.target.value)}
								className="col-span-3"
								placeholder="z.B. 001"
								required
								autoFocus
								title="Code für Anzeige auf Dokumenten"
							/>
						</div>
						<div className="grid grid-cols-4 items-center gap-4">
							<Label htmlFor="code_e" className="text-right">
								Eingabe
							</Label>
							<Input
								id="code_e"
								value={codeE}
								onChange={(e) => setCodeE(e.target.value)}
								className="col-span-3"
								placeholder="z.B. 001A"
								required
								title="Code für Eingabe (muss eindeutig sein)"
							/>
						</div>
					</div>
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => handleOpenChange(false)}
							disabled={isSubmitting}
						>
							Abbrechen
						</Button>
						<Button type="submit" disabled={isSubmitting || !code.trim() || !codeE.trim()}>
							{isSubmitting ? "Erstelle..." : "Erstellen"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};
